/**
 * Discussion feature that appears below the content on content pages.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import debounce from "lodash.debounce";
import {compose} from "mu-lambda";
import * as React from "react";
import {findDOMNode} from "react-dom";
import {connect} from "react-redux";

import {View} from "@khanacademy/wonder-blocks-core";
import {markMinorConversions} from "@khan/analytics-learning-time";
import {SkipLink} from "@khan/component-skip-link";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {standardTransitionMs} from "@khan/shared-styles";
import {LocalStore} from "@khan/util-local-store";
import {scrollToElement} from "@khan/util-scrolling";

import type {OfficialClarification} from "#clarifications-package/types.ts";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import {discussionContainer} from "../shared-styles.ts";
import type {DiscussionContext, FeedbackType} from "../types.ts";
import {getModifiedDiscussionContext, userHasJustLoggedIn} from "../util.tsx";
import type {ViewHandlerContentPath} from "../util/content-path-converter.ts";

import {
    changeTabAction,
    fetchPostsAction,
    initializeContentAction,
    initializeSortAction,
} from "./discussion-actions.ts";
import type {State} from "./discussion-reducer.ts";
import DiscussionTabs from "./discussion-tabs.tsx";
import {DiscussionViewHandler} from "./discussion-view-handler.tsx";

const discussionSkippedId = "skipped-discussion";

type DefaultProps = {
    discussionContext: DiscussionContext;
};

type OwnProps = DefaultProps & {
    contentKind: string;
    contentId: string;
    contentPath?: ViewHandlerContentPath;
    topicSlug: string;
    youtubeId?: string;
    onNextPage: () => void;
    clarificationsEnabled?: boolean;
};

type ReduxProps = {
    // from redux
    tab: FeedbackType;
    acceptedClarificationPosts: Array<OfficialClarification>;
    onInitializeContent: (
        topicSlug: string,
        contentKind: string,
        contentId: string,
        youtubeId?: string,
        expandFeedbackType?: FeedbackType,
        qaExpandKey?: string,
    ) => void;
    onInitializePosts: (gqlClient: ApolloGqlClient) => void;
    onTabChanged: (newTabKey: FeedbackType) => void;
    onInitializeSort: (kaid: string) => void;
};

type Props = OwnProps & ReduxProps & WithUserProfileProps & WithApolloProps;

class Discussion extends React.Component<Props> {
    discussionRoot: HTMLElement | null | undefined;
    // @ts-expect-error - TS2564 - Property '_debouncedMaybeLogConversion' has no initializer and is not definitely assigned in the constructor.
    _debouncedMaybeLogConversion: () => void;

    static defaultProps: DefaultProps = {
        discussionContext: {},
    };

    componentDidMount() {
        this.handleInitializeContent();

        // In order for qa_expand_key links to work, we need to initialize the
        // discussion content without waiting for the user to scroll.
        if (shouldPreInitializePosts(this.props.discussionContext)) {
            this.handleInitializePosts();
        }
        this._debouncedMaybeLogConversion = debounce(
            this.maybeLogConversion,
            500,
        );
        document.addEventListener("scroll", this._debouncedMaybeLogConversion);
        this._debouncedMaybeLogConversion();

        const {discussionRoot} = this;
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // @ts-expect-error - TS2345 - Argument of type 'Location' is not assignable to parameter of type '{ [href: string]: string; }'.
        // eslint-disable-next-line no-restricted-syntax
        if (discussionRoot && userHasJustLoggedIn(window.location)) {
            const duration = standardTransitionMs * 2;
            // We use a setTimeout with 0 ms to make sure this method call happens
            // after the discussionRoot element is actually ready to be focused on.
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(() => scrollToElement(discussionRoot, duration), 0);
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.contentId !== prevProps.contentId) {
            this.handleInitializeContent();
            if (shouldPreInitializePosts(this.props.discussionContext)) {
                this.handleInitializePosts();
            }
        }
    }

    componentWillUnmount() {
        // We update the tab in the store on componentWillUnmount instead of componentDidUpdate
        // because the Discussion component remounts on content change but the store
        // persists with the tab unchanged. We also don't want to set the tab in componentDidMount
        // because the tab becomes focused on change, and we don't want this tab to be
        // focused on when the content first loads. The remaining option, then, is to
        // reset the tab before removing the component so the store's all set when the
        // next version of the component is mounted.
        this.props.onTabChanged("questions");

        // Clean up after ourselves.
        document.removeEventListener(
            "scroll",
            this._debouncedMaybeLogConversion,
        );
    }

    _markedConversion = false;

    handleInitializeContent: () => void = () => {
        const {
            onInitializeContent,
            topicSlug,
            contentKind,
            contentId,
            youtubeId,
            discussionContext,
        } = this.props;
        const kaid = this.props.profile?.get("kaid");
        if (kaid) {
            const storedSort = LocalStore.get("sort_type:" + kaid);
            if (storedSort) {
                this.props.onInitializeSort(storedSort);
            }
        }

        onInitializeContent(
            topicSlug,
            contentKind,
            contentId,
            youtubeId,
            discussionContext?.expandFeedbackType,
            discussionContext?.qaExpandKey,
        );
    };

    handleInitializePosts: () => void = () => {
        this.props.onInitializePosts(this.props.gqlClient);
    };

    // (ned): This doesn't seem to fire as expected. Perhaps we should remove it
    // and move the call to the new DiscussionViewHandler component?
    maybeLogConversion: () => void = () => {
        if (!this.discussionRoot || this._markedConversion) {
            return;
        }

        const discussionRect = this.discussionRoot.getBoundingClientRect();
        const discussionTop = discussionRect.top;

        // We're only checking if the top of the discussion container has been scrolled
        // above the bottom of the window because even partial visibility is enough
        // for the user to interact with the feature.
        const isVisible = discussionTop <= window.innerHeight;

        if (isVisible) {
            markMinorConversions([
                {
                    id: "content_discussions_view",
                    extra: {
                        contentKind: this.props.contentKind,
                    },
                },
            ]);

            this._markedConversion = true;
            document.removeEventListener(
                "scroll",
                this._debouncedMaybeLogConversion,
            );
        }
    };

    render(): React.ReactNode {
        const {
            profile,
            discussionContext,
            acceptedClarificationPosts,
            contentKind,
            contentId,
            tab,
            clarificationsEnabled,
            topicSlug,
            contentPath,
        } = this.props;

        const modifiedDiscussionContext = getModifiedDiscussionContext(
            profile,
            discussionContext,
        );

        return (
            <View
                style={styles.container}
                ref={(node) =>
                    (this.discussionRoot = findDOMNode(node) as HTMLElement)
                }
                tabIndex={-1}
            >
                <SkipLink
                    targetSelector={`#${discussionSkippedId}`}
                    translatedText={t`Skip to end of discussions`}
                />
                <DiscussionViewHandler
                    contentPath={
                        contentPath ?? {
                            domainId: null,
                            courseId: null,
                            unitId: null,
                            lessonId: null,
                            learnableNodeKind: contentKind,
                            learnableNodeId: contentId,
                        }
                    }
                    handleInitializePosts={this.handleInitializePosts}
                    preInitialized={shouldPreInitializePosts(
                        this.props.discussionContext,
                    )}
                >
                    <DiscussionTabs
                        acceptedClarificationPosts={acceptedClarificationPosts}
                        contentKind={contentKind}
                        discussionContext={modifiedDiscussionContext}
                        tab={tab}
                        clarificationsEnabled={!!clarificationsEnabled}
                        contentId={contentId}
                        topicSlug={topicSlug}
                    />
                </DiscussionViewHandler>
                <span tabIndex={-1} id={discussionSkippedId} />
            </View>
        );
    }
}

const shouldPreInitializePosts = (context: DiscussionContext) =>
    !!context?.qaExpandKey;

const styles = StyleSheet.create({
    container: {
        ...discussionContainer,
        paddingBottom: 8,
    },
});

const mapDispatchToProps = {
    onInitializeSort: initializeSortAction,
    onInitializeContent: initializeContentAction,
    onInitializePosts: fetchPostsAction,
    onTabChanged: changeTabAction,
} as const;

const mapStateToProps = (state: State) => {
    return {
        tab: state.currentTab,
        acceptedClarificationPosts: state.acceptedClarificationPosts,
    };
};

type ExportProps = WithoutUserProfile<
    WithoutApollo<
        Without<
            JSX.LibraryManagedAttributes<
                typeof Discussion,
                React.ComponentProps<typeof Discussion>
            >,
            ReduxProps
        >
    >
>;

export default compose(
    withUserProfile,
    withApollo,
    connect(mapStateToProps, mapDispatchToProps),
)(Discussion) as React.ComponentType<ExportProps>;
